import React, { useEffect, useRef, useState } from 'react'
import styled, { keyframes } from 'styled-components'
import { getProcessingUploads } from 'services/BpService'
import { useFilesRefresh } from 'utils/uploadEvents'
import UploadFailuresList, { failureLabel } from 'features/bpDetails/Components/files/UploadFailuresList'
import { RAG } from 'features/dashboard/charts/scoreColour'

const POLL_MS = 20000

const dismissKey = (type, id) => `upload-failures-dismissed:${type}:${id}`

const readDismissed = (type, id) => {
  try { return JSON.parse(sessionStorage.getItem(dismissKey(type, id)) || '[]') } catch { return [] }
}

const writeDismissed = (type, id, ids) => {
  try { sessionStorage.setItem(dismissKey(type, id), JSON.stringify(ids)) } catch { }
}

const ProcessingFilesPill = ({ type = 'building_passport', id, onCleared, onRetry }) => {
  const [count, setCount] = useState(0)
  const [failedFiles, setFailedFiles] = useState([])
  const [dismissedIds, setDismissedIds] = useState(() => readDismissed(type, id))
  const [showFailures, setShowFailures] = useState(false)
  const prevRef = useRef(null)
  const clearedRef = useRef(onCleared)
  clearedRef.current = onCleared
  const pollNowRef = useRef(null)

  useEffect(() => {
    if (!id) return undefined
    let active = true
    let timer = null
    let generation = 0

    const poll = () => {
      const current = ++generation
      getProcessingUploads(type, id)
        .then((res) => {
          if (!active || current !== generation) return
          const next = Number(res?.data?.data?.processing ?? 0) || 0
          if (prevRef.current !== null && next !== prevRef.current) clearedRef.current?.()
          prevRef.current = next
          setCount(next)
          setFailedFiles(Array.isArray(res?.data?.data?.failedFiles) ? res.data.data.failedFiles : [])
        })
        .catch(() => {})
        .finally(() => {
          if (active && current === generation) timer = setTimeout(poll, POLL_MS)
        })
    }

    poll()

    pollNowRef.current = () => {
      if (!active) return
      if (timer) clearTimeout(timer)
      timer = null
      poll()
    }

    return () => {
      active = false
      pollNowRef.current = null
      if (timer) clearTimeout(timer)
    }
  }, [type, id])

  useEffect(() => {
    prevRef.current = null
    setDismissedIds(readDismissed(type, id))
    setFailedFiles([])
    setShowFailures(false)
  }, [type, id])

  useFilesRefresh(type === 'building_passport' ? id : null, () => pollNowRef.current?.())

  const visibleFailures = failedFiles.filter(f => !dismissedIds.includes(f.id))
  const dismissFailures = () => {
    const ids = Array.from(new Set([...dismissedIds, ...failedFiles.map(f => f.id)]))
    writeDismissed(type, id, ids)
    setDismissedIds(ids)
    setShowFailures(false)
  }

  if (!count && !visibleFailures.length) return null

  return (
    <>
      {visibleFailures.length > 0 && (
        <FailureNotice role='status' aria-live='polite'>
          <FailureHeader>
            <FailureTitle>{failureLabel(visibleFailures.length)}</FailureTitle>
            <FailureActions>
              <FailureButton type='button' aria-expanded={showFailures} onClick={() => setShowFailures(v => !v)}>
                {showFailures ? 'Hide the list' : 'See which files'}
              </FailureButton>
              <FailureButton type='button' $plain onClick={dismissFailures} aria-label='Dismiss this message about uploads that could not be saved'>
                Dismiss
              </FailureButton>
            </FailureActions>
          </FailureHeader>
          <FailureHint>Your other documents are fine. You can upload these files again whenever you are ready.</FailureHint>
          {showFailures && (
            <FailureListScroll>
              <UploadFailuresList failures={visibleFailures} onRetry={onRetry} retryLabel='Upload these again' />
            </FailureListScroll>
          )}
        </FailureNotice>
      )}
      {count > 0 && (
        <Bar>
        <Pill role='status' aria-live='polite'>
          <Spinner aria-hidden='true' />
          <Text>
            <Strong>
              {count === 1
                ? '1 document is still being prepared'
                : `${count} documents are still being prepared`}
            </Strong>
            <Sub>They will appear here on their own in a few minutes. There is no need to upload them again.</Sub>
          </Text>
        </Pill>
        </Bar>
      )}
    </>
  )
}

export default ProcessingFilesPill

const Bar = styled.div`
  position: sticky;
  top: 0;
  z-index: 4;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  box-sizing: border-box;
  max-width: 100%;
  padding: 10px 0 6px;
  background: ${({ theme }) => theme.colors?.white || '#ffffff'};
`

const spin = keyframes`
  to { transform: rotate(360deg); }
`

const Pill = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 12px;
  max-width: 100%;
  padding: 10px 18px 10px 14px;
  border-radius: 999px;
  background: #e6f7f9;
  border: 1px solid ${({ theme }) => theme.colors?.secondary || '#33becc'}73;
  box-shadow: 0 1px 3px rgba(16, 24, 40, 0.08);

  @media (max-width: 768px) {
    width: 100%;
    border-radius: 14px;
    align-items: flex-start;
  }
`

const Text = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1px;
  min-width: 0;
`

const Strong = styled.span`
  font-size: 15px;
  font-weight: 600;
  color: #1c1c22;
`

const Sub = styled.span`
  font-size: 13px;
  line-height: 1.4;
  color: #5f5f6a;
`

const Spinner = styled.span`
  width: 16px;
  height: 16px;
  flex: 0 0 auto;
  border-radius: 50%;
  background: conic-gradient(${({ theme }) => theme.colors?.secondary || '#33becc'} 0 75%, transparent 75% 100%);
  -webkit-mask: radial-gradient(circle 5px at 50% 50%, transparent 98%, #000);
          mask: radial-gradient(circle 5px at 50% 50%, transparent 98%, #000);
  animation: ${spin} 1s linear infinite;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
    opacity: 0.55;
  }
`

const FailureListScroll = styled.div`
  max-height: 40vh;
  overflow-y: auto;
  overscroll-behavior: contain;
`

const FailureNotice = styled.div`
  box-sizing: border-box;
  width: min(640px, 100%);
  margin: 10px auto 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px 14px;
  border-radius: 14px;
  border: 1px solid ${RAG.critical.fg}40;
  background: #ffffff;
  box-shadow: 0 1px 3px rgba(16, 24, 40, 0.08);
`

const FailureHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  flex-wrap: wrap;
`

const FailureTitle = styled.span`
  font-size: 15px;
  font-weight: 700;
  color: ${RAG.critical.fg};
`

const FailureActions = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`

const FailureButton = styled.button`
  min-height: 40px;
  padding: 8px 14px;
  border-radius: 8px;
  border: 1px solid ${({ $plain }) => ($plain ? '#d4d4d8' : RAG.critical.fg)};
  background: #ffffff;
  color: ${({ $plain }) => ($plain ? '#3f3f46' : RAG.critical.fg)};
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
`

const FailureHint = styled.span`
  font-size: 14px;
  line-height: 1.45;
  color: #3f3f46;
`
